import type { TradeAccountOption } from "../contexts/ActiveBrokerAccountContext";
import type { TradeAccountAnalytics } from "../services/brokerIntegrationApi";

function normalizeBrokerSlug(brokerName: string): string {
  const s = String(brokerName || "").toLowerCase();
  return s === "liquid" ? "liquidbrokers" : s;
}

/** Active row from GET /broker/trade-accounts (server-resolved `analytics`). */
export function findTradeAccount(
  accounts: TradeAccountOption[],
  accountCode: string | null | undefined,
  brokerName: string | null | undefined
): TradeAccountOption | null {
  const code = (accountCode || "").trim();
  if (!code) return null;
  const bn = brokerName ? normalizeBrokerSlug(brokerName) : null;
  const matches = accounts.filter((a) => a.accountCode === code);
  if (matches.length === 0) return null;
  if (bn) {
    return matches.find((a) => normalizeBrokerSlug(a.brokerName) === bn) ?? null;
  }
  return matches.length === 1 ? matches[0] : null;
}

export function getTradeAccountAnalytics(
  account: TradeAccountOption | null | undefined
): TradeAccountAnalytics | null {
  return account?.analytics ?? null;
}

/** Daily activity / calendar TZ: CSV statement zone when present, else user reporting TZ. */
export function resolveDailyActivityTimezone(
  analytics: TradeAccountAnalytics | null | undefined,
  userTimezone: string
): string {
  if (analytics?.data_source === "csv_import" && analytics.statement_time_zone?.trim()) {
    return analytics.statement_time_zone.trim();
  }
  return userTimezone;
}

export function supportsPortfolioGrowthFromAnalytics(
  analytics: TradeAccountAnalytics | null | undefined,
  /** While accounts are loading, avoid flashing the trading-only strip. */
  accountsLoading?: boolean
): boolean {
  if (accountsLoading && analytics == null) return true;
  if (analytics == null) return true;
  return analytics.supports_portfolio_growth === true;
}

export function supportsPortfolioBalanceFromBrokerAnalytics(
  analytics: TradeAccountAnalytics | null | undefined
): boolean {
  return analytics?.supports_portfolio_balance_from_broker !== false;
}
